import {
  OVERVIEW_MAX_RANGE_DAYS,
  RUNDOWN_DAYS_DEFAULT,
  RUNDOWN_DAYS_MAX,
  RUNDOWN_DAYS_MIN,
  RUNDOWN_KINDS,
  daysError,
  overviewError,
  overviewPresetOptions,
  rundownDaysSteps,
  rundownDaysText,
  stepForArrowKey,
  stepRundownDays,
  typedRundownDays,
  type OverviewPeriod,
  type PeriodPresetId,
  type RundownKind,
  type RundownPeriod,
} from '@brush/ui';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import type { PeriodFocus } from '../../nav';
import { useMainReveal } from '../../scroll';
import { modalControl } from '../Modal';
import { usePeriodChoice } from '../../periods/context';

/**
 * The Periods card, third in Settings → Preferences (docs/specs/user-preferences.md §3, UP23–UP25, A6): the one place
 * the Rundown period and the Overview totals period are chosen. The Rundown and Overview only state them, with a
 * Change link here. Every staff user reaches it, an administrator and a room operator alike.
 *
 * Held for the session only (UP25): a reload starts from All upcoming and This month, and the card says so in its own
 * line, distinct from the card beside it, which is saved in this browser. Part 1b saves these same choices, under the
 * same keys, from this card.
 *
 * A value the server would refuse is never applied: a typed number of days or a custom range that is not valid stays
 * as typed with the reason beside it, and the choice in force stays the last valid one.
 */
export function PeriodsCard({ focus }: { focus: PeriodFocus | null }) {
  const section = useRef<HTMLElement>(null);
  const rundownSelect = useRef<HTMLSelectElement>(null);
  const totalsSelect = useRef<HTMLSelectElement>(null);
  const reveal = useMainReveal();

  // A Change link SHOWS the card (A6b): scrolled into view, focus on the field that sets the period beside the link.
  // A frame later rather than now, because the shell and Settings scroll the content to the top on a section or pane
  // change, and as ancestors their effects run after this one. No motion when the reader asks for reduced motion.
  // The shell's `<main>` is moved, and nothing else: the browser's own reveal scrolls every ancestor, the window too (A6c).
  useEffect(() => {
    if (!focus) return;
    const frame = requestAnimationFrame(() => {
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      if (section.current) reveal(section.current, reduced);
      (focus.field === 'rundown' ? rundownSelect : totalsSelect).current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [focus, reveal]);

  return (
    <section ref={section} className="max-w-3xl rounded-xl border border-ink-800/10 bg-white p-6 shadow-sm">
      <h2 className="font-display font-semibold text-ink-950">Periods</h2>
      <p className="mt-0.5 text-xs text-ink-800/60">
        These last until you reload the page; saving them comes later.
      </p>
      <RundownPeriodField select={rundownSelect} />
      <TotalsPeriodField select={totalsSelect} />
    </section>
  );
}

const fieldHeading = 'mb-1 text-[10px] uppercase tracking-wider text-ink-800/50';

/**
 * Next N days is a stepper (UP23): the field, a down and an up button, and the arrow keys in the field. A step starts
 * from the period in force — always valid — so it never depends on what is typed, and the bounds stop it: down is
 * disabled at 1, up at 90, and the keys do nothing past them.
 */
function RundownPeriodField({ select }: { select: RefObject<HTMLSelectElement | null> }) {
  const [period, setPeriod] = usePeriodChoice('rundown.period');
  const [daysText, setDaysText] = useState(() => rundownDaysText(period));
  const id = useId();
  const applied = period.kind === 'nextDays' ? period.days : null;
  const error = applied !== null ? daysError(daysText) : null;
  const steps = applied !== null ? rundownDaysSteps(applied) : { up: false, down: false };

  const choose = (kind: RundownKind) => {
    if (kind !== 'nextDays') {
      setPeriod({ kind } as RundownPeriod);
      return;
    }
    const typed = typedRundownDays(daysText);
    if (typed === null) setDaysText(String(RUNDOWN_DAYS_DEFAULT));
    setPeriod({ kind: 'nextDays', days: typed ?? RUNDOWN_DAYS_DEFAULT });
  };

  const type = (text: string) => {
    setDaysText(text);
    const days = typedRundownDays(text);
    if (days !== null) setPeriod({ kind: 'nextDays', days });
  };

  const step = (by: 1 | -1) => {
    if (applied === null) return;
    const days = stepRundownDays(applied, by);
    if (days === null) return;
    setDaysText(String(days));
    setPeriod({ kind: 'nextDays', days });
  };

  const stepButton = 'grid size-9 place-items-center border border-ink-800/15 bg-white text-ink-800/70 hover:text-ink-950 disabled:opacity-40';

  return (
    <div className="mt-5 border-t border-ink-800/10 pt-4">
      <label htmlFor={`${id}-kind`} className={`block ${fieldHeading}`}>
        Rundown period
      </label>
      <p className="mb-3 text-xs text-ink-800/60">How far forward the Rundown looks, from the room's day.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          ref={select}
          id={`${id}-kind`}
          value={period.kind}
          onChange={(e) => choose(e.target.value as RundownKind)}
          className={`${modalControl} w-44 bg-white`}
        >
          {RUNDOWN_KINDS.map((k) => (
            <option key={k.kind} value={k.kind}>
              {k.label}
            </option>
          ))}
        </select>
        {applied !== null ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="One day fewer"
              disabled={!steps.down}
              onClick={() => step(-1)}
              className={stepButton}
            >
              <ChevronDown className="size-4" aria-hidden />
            </button>
            <label className="sr-only" htmlFor={`${id}-days`}>
              Number of days
            </label>
            <input
              id={`${id}-days`}
              inputMode="numeric"
              autoComplete="off"
              value={daysText}
              onChange={(e) => type(e.target.value)}
              onKeyDown={(e) => {
                const by = stepForArrowKey(e.key);
                if (by === null) return;
                e.preventDefault();
                step(by);
              }}
              aria-invalid={error !== null}
              aria-describedby={`${id}-days-hint`}
              className={`${modalControl} w-16 bg-white text-right`}
            />
            <button
              type="button"
              aria-label="One day more"
              disabled={!steps.up}
              onClick={() => step(1)}
              className={stepButton}
            >
              <ChevronUp className="size-4" aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
      {applied !== null ? (
        <p id={`${id}-days-hint`} className={`mt-1.5 text-xs ${error ? 'text-ink-950' : 'text-ink-800/60'}`}>
          {error ?? `Days, ${RUNDOWN_DAYS_MIN} to ${RUNDOWN_DAYS_MAX}.`}
        </p>
      ) : null}
    </div>
  );
}

const CUSTOM = 'custom';
const ALL_TIME = 'allTime';

function selected(period: OverviewPeriod): string {
  if ('kind' in period) return ALL_TIME;
  return 'preset' in period ? period.preset : CUSTOM;
}

/**
 * The Overview totals period (UP24): All time, the presets the cap allows (all nine at 31 days), or a custom range of
 * both dates, at most the cap. It moves the Tournaments, Prize pool and Total entries cards and nothing else.
 */
function TotalsPeriodField({ select }: { select: RefObject<HTMLSelectElement | null> }) {
  const [period, setPeriod] = usePeriodChoice('overview.totals');
  const [mode, setMode] = useState(selected(period));
  const [range, setRange] = useState(() => ('from' in period ? { from: period.from, to: period.to } : { from: '', to: '' }));
  const id = useId();
  const error = mode === CUSTOM ? overviewError(range) : null;

  const choose = (value: string) => {
    setMode(value);
    if (value === ALL_TIME) setPeriod({ kind: 'allTime' });
    else if (value === CUSTOM) {
      if (overviewError(range) === null) setPeriod({ from: range.from, to: range.to });
    } else setPeriod({ preset: value as PeriodPresetId });
  };

  const typeRange = (edge: 'from' | 'to', value: string) => {
    const next = { ...range, [edge]: value };
    setRange(next);
    if (overviewError(next) === null) setPeriod({ from: next.from, to: next.to });
  };

  return (
    <div className="mt-5 border-t border-ink-800/10 pt-4">
      <label htmlFor={`${id}-period`} className={`block ${fieldHeading}`}>
        Overview totals
      </label>
      <p className="mb-3 text-xs text-ink-800/60">
        Which period Overview's Tournaments, Prize pool and Total entries cards show, each compared with the period
        before. The player figures and the status cards are not affected.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          ref={select}
          id={`${id}-period`}
          value={mode}
          onChange={(e) => choose(e.target.value)}
          className={`${modalControl} w-44 bg-white`}
        >
          <option value={ALL_TIME}>All time</option>
          {overviewPresetOptions().map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
          <option value={CUSTOM}>Custom range</option>
        </select>
        {mode === CUSTOM ? (
          <>
            <label className="sr-only" htmlFor={`${id}-from`}>
              Start date
            </label>
            <input
              id={`${id}-from`}
              type="date"
              value={range.from}
              onChange={(e) => typeRange('from', e.target.value)}
              aria-invalid={error !== null}
              aria-describedby={`${id}-hint`}
              className={`${modalControl} w-40 bg-white`}
            />
            <span className="text-xs text-ink-800/40">to</span>
            <label className="sr-only" htmlFor={`${id}-to`}>
              End date
            </label>
            <input
              id={`${id}-to`}
              type="date"
              value={range.to}
              onChange={(e) => typeRange('to', e.target.value)}
              aria-invalid={error !== null}
              aria-describedby={`${id}-hint`}
              className={`${modalControl} w-40 bg-white`}
            />
          </>
        ) : null}
      </div>
      {mode === CUSTOM ? (
        <p id={`${id}-hint`} className={`mt-1.5 text-xs ${error ? 'text-ink-950' : 'text-ink-800/60'}`}>
          {error ?? `At most ${OVERVIEW_MAX_RANGE_DAYS} days.`}
        </p>
      ) : null}
    </div>
  );
}
